import { useState, useCallback, useMemo } from "react";
import {
  useLoaderData,
  useNavigation,
  type LoaderFunctionArgs,
  useSearchParams,
} from "react-router-dom";
import { ClicksGrid } from "../components/clicks/ClicksGrid";
import { ClicksChart } from "../components/clicks/ClicksChart";
import { GscFreshnessBadge } from "../components/clicks/GscFreshnessBadge";
import { ATTRIBUTE_SOURCES, DEFAULT_VISIBLE_METRICS } from "@/config/metrics";
import { useAttributeSources } from "@/hooks/useAttributeSources";
import { MetricsFilter } from "@/components/clicks/MetricsFilters";
import { GroupByFilter } from "@/components/clicks/GroupByFilter";
import { SavedViews } from "@/components/clicks/SavedViews";
import { FilterBar } from "@/components/clicks/filters/FilterBar";
import type { SavedView } from "@/lib/saved-views";
import { CustomMetricsManager } from "@/components/clicks/CustomMetricsManager";
import {
  getDimensionLabel,
  getVisibleMetrics,
  isAttributeColumn,
  metricLabel,
  setCustomMetricRegistry,
} from "@/components/clicks/metricDisplay";
import {
  appliesToGrouping,
  liveColumnFilters,
  mergeColumnFilters,
  TEXT_COLUMNS,
  type ColumnFilter,
} from "@/components/clicks/clicksGridEngine";
import type { ColumnOption } from "@/components/clicks/filters/FilterBuilder";
import { useUrlFilters } from "@/hooks/useUrlFilters";
import { COMPARE_MAX } from "@/components/clicks/chartData";
import {
  loadCustomMetrics,
  persistCustomMetrics,
  type CustomMetric,
} from "@/lib/custom-metrics";
import { PipelineFreshness } from "@/components/clicks/PipelineFreshness";
import { LoadingOverlay } from "@/components/ui/spinner";
import { DateRangeFilter } from "../components/clicks/DateRangeFilter";
import { queryClient } from "@/lib/queryClient";
import { cn } from "@/lib/utils";
import { useVolumeAnchor } from "@/hooks/useKeywordVolumes";
import {
  CLICKS_STATS_STALE_TIME,
  EMPTY_CLICKS_STATS,
  fetchClicksAllStats,
  getClicksAllStatsQueryKey,
  parseClicksParamsFromUrl,
  useClicksAllStats,
  useGscDomainMetrics,
  normDomainKey,
  type ClicksLoaderData,
} from "@/hooks/useClicksData";

export async function clicksLoader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const params = parseClicksParamsFromUrl(url);

  // Start the grid's one all-rows query while the route loads, but do not
  // wait for it: a multi-second aggregation must not hold the navigation - the
  // page shows its loading state instead. Until 08.10.2026 this awaited the
  // paged get_dynamic_stats, which nothing read: a second full aggregation on
  // every visit.
  void queryClient.prefetchQuery({
    queryKey: getClicksAllStatsQueryKey(params),
    queryFn: () => fetchClicksAllStats(params),
    staleTime: CLICKS_STATS_STALE_TIME,
  });

  return params;
}

export default function ClicksPage() {
  const params = useLoaderData() as ClicksLoaderData;
  const navigation = useNavigation();
  useVolumeAnchor(); // keyword volume: month labels for kv_m1..kv_m12
  const [searchParams, setSearchParams] = useSearchParams();
  const [visibleColumns, setVisibleColumns] = useState<string[]>(
    DEFAULT_VISIBLE_METRICS,
  );
  // User-defined metrics (per-browser). Registered with metricDisplay so the
  // grid, picker and export treat them like built-in columns; ClicksGrid
  // computes their per-row values once keyword volume is merged in.
  const [customMetrics, setCustomMetrics] = useState<CustomMetric[]>(() =>
    loadCustomMetrics(),
  );
  setCustomMetricRegistry(
    customMetrics.map((m) => ({ id: m.id, label: m.name, format: m.format })),
  );
  const changeCustomMetrics = useCallback(
    (next: CustomMetric[]) => {
      const prevIds = new Set(customMetrics.map((m) => m.id));
      const nextIds = new Set(next.map((m) => m.id));
      const added = next.filter((m) => !prevIds.has(m.id)).map((m) => m.id);
      const removed = customMetrics
        .filter((m) => !nextIds.has(m.id))
        .map((m) => m.id);
      setCustomMetrics(persistCustomMetrics(next));
      // Show a new metric, and drop a deleted one from the visible columns.
      // Dropping is required, not cosmetic: it re-keys ClicksGrid's visibleMetrics
      // memo (keyed on visibleColumns) so the removed column actually leaves.
      if (added.length || removed.length)
        setVisibleColumns((cols) => [
          ...cols.filter((id) => !removed.includes(id)),
          ...added.filter((id) => !cols.includes(id)),
        ]);
    },
    [customMetrics],
  );
  // Drill-down: which table value the trend chart is scoped to (context menu).
  const [chartFocus, setChartFocus] = useState<{
    key: string;
    value: string;
    label: string;
  } | null>(null);
  // Row selection: the dimension values checked in the table (any number; the
  // header checkbox selects every filtered row). The chart overlays only the
  // first COMPARE_MAX of them - more lines than that is noise, not comparison.
  const [compareValues, setCompareValues] = useState<string[]>([]);
  const toggleCompare = useCallback((value: string) => {
    setCompareValues((cur) =>
      cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value],
    );
  }, []);
  const chartCompareValues = useMemo(
    () => compareValues.slice(0, COMPARE_MAX),
    [compareValues],
  );

  const {
    data: stats,
    isPending: isStatsPending,
    isPlaceholderData: isStatsPlaceholder,
    isError: isStatsError,
  } = useClicksAllStats(params);
  const { data: gscMap } = useGscDomainMetrics(params);

  const {
    data,
    previousData,
  } = stats ?? EMPTY_CLICKS_STATS;
  const {
    limit,
    groupBy,
    groupDims,
    dateRange,
    compareEnabled,
    customFrom,
    customTo,
  } = params;

  // Column conditions (the header filters, "Revenue > 1000") live in the URL
  // next to the filters and show as chips in the filter bar; they narrow the
  // table's rows only (owner, 30.10.2026). What applies now: the first
  // column's under its own grouping, the rest while their column is shown.
  // Filters that do not apply now (another grouping's, a hidden column's)
  // stay in the URL and come back with their grouping or column.
  const { where, update: updateFilters } = useUrlFilters();
  const dimsKey = (groupDims?.length ? groupDims : [groupBy]).join(",");
  const columnContext = useMemo(
    () => ({ dims: dimsKey.split(","), columns: new Set(visibleColumns) }),
    [dimsKey, visibleColumns],
  );
  const columnFilters = useMemo(
    () => liveColumnFilters(where, columnContext),
    [where, columnContext],
  );
  const changeColumnFilters = useCallback(
    (next: Record<string, ColumnFilter>) =>
      updateFilters({ where: (cur) => mergeColumnFilters(cur, next, columnContext) }),
    [updateFilters, columnContext],
  );
  const columnOptions = useMemo<ColumnOption[]>(
    () => [
      { id: "dimension", label: dimsKey.split(",").map(getDimensionLabel).join(" ▸ "), kind: "text" },
      ...(visibleColumns.includes("brand") ? [{ id: "brand", label: "Brand", kind: "text" as const }] : []),
      ...(visibleColumns.includes("campaign_group")
        ? [{ id: "campaign_group", label: "Campaign Group", kind: "text" as const }]
        : []),
      ...visibleColumns.filter(isAttributeColumn).map((id) => ({
        id,
        label: metricLabel(id),
        kind: TEXT_COLUMNS.has(id) ? ("text" as const) : ("number" as const),
      })),
      ...getVisibleMetrics(visibleColumns).map((m) => ({
        id: m.id,
        label: metricLabel(m.id, m.label),
        kind: TEXT_COLUMNS.has(m.id) ? ("text" as const) : ("number" as const),
      })),
    ],
    [dimsKey, visibleColumns],
  );

  // Attribute sources (Domain Tracker, ...): each fetched while one of its
  // columns is shown and the rows have a domain - grouped by Domain or URL, in
  // any position.
  const rowDims = dimsKey.split(",");
  const rowsHaveDomain = rowDims.includes("domain") || rowDims.includes("url");
  // A source is fetched only for a shown column that applies to this grouping.
  const shownSources = rowsHaveDomain
    ? ATTRIBUTE_SOURCES.filter((src) =>
        src.columns.some((c) => visibleColumns.includes(c.id) && appliesToGrouping(c.id, rowDims)),
      ).map((src) => src.id)
    : [];
  const shownKey = shownSources.join(",");
  const { maps: attrMaps, failedTitles: attrFailed } = useAttributeSources(shownSources);

  // GSC metrics merge client-side onto domain rows by normalized domain. Only
  // when grouped by domain (the shared key); other groupings pass through.
  // Attribute sources merge by the row's domain the same way: the domain part
  // of a two-level row, a URL's host (normDomainKey drops the path).
  const mergedData = useMemo(() => {
    const withGsc =
      groupBy !== "domain" || !gscMap || gscMap.size === 0
        ? data
        : data.map((row) => {
            const g = gscMap.get(normDomainKey(row.dimension_value));
            return g ? { ...row, ...g } : row;
          });
    const shown = shownKey ? shownKey.split(",") : [];
    const sources = ATTRIBUTE_SOURCES.flatMap((src, i) => {
      const map = attrMaps[i];
      return map && shown.includes(src.id) ? [{ src, map }] : [];
    });
    if (!sources.length) return withGsc;
    const dims = dimsKey.split(",");
    const at = dims.includes("domain") ? dims.indexOf("domain") : dims.indexOf("url");
    return withGsc.map((row) => {
      const v = dims.length > 1 ? row.dimension_parts?.[at] : row.dimension_value;
      if (!v) return row;
      const key = normDomainKey(v);
      let out = row;
      for (const { src, map } of sources) {
        const a = map.get(key);
        if (a) out = { ...out, ...Object.fromEntries(src.columns.map((c, i) => [c.id, a[i] ?? null])) };
      }
      return out;
    });
  }, [data, gscMap, groupBy, attrMaps, shownKey, dimsKey]);
  // A composite row's parts, so the chart scopes a compared row by them
  // rather than by splitting its label.
  const compareParts = useMemo(
    () => new Map(data.map((row) => [row.dimension_value, row.dimension_parts])),
    [data],
  );

  // Checked values belong to the current grouping (domains, campaigns…); when
  // the grouping changes they're meaningless, so drop them. Keyed on the whole
  // dimension signature, not just groupBy, so switching between multi-dim
  // groupings (country ▸ brand → country ▸ domain) also clears stale composites.
  // Done at render time (React's "adjust state on prop change"), not in an effect.
  const groupSignature = (groupDims?.length ? groupDims : [groupBy]).join(",");
  const [compareGroupSig, setCompareGroupSig] = useState(groupSignature);
  if (compareGroupSig !== groupSignature) {
    setCompareGroupSig(groupSignature);
    setCompareValues([]);
  }

  const isRouteLoading = navigation.state === "loading";
  // isPlaceholderData: new dates/grouping requested while the previous rows
  // are still on screen (keepPreviousData). Not isFetching - that would also
  // dim the table during the quiet hourly refetch at :03.
  const isLoadingData = isRouteLoading || isStatsPending || isStatsPlaceholder;

  const handleParamChange = useCallback(
    (key: string, value: string) => {
      setSearchParams((prev) => {
        prev.set(key, value);
        if (key !== "page") prev.set("page", "1");
        return prev;
      });
    },
    [setSearchParams],
  );

  const handleGroupDimsChange = useCallback(
    (dims: string[]) => {
      setSearchParams((prev) => {
        // groupDims is the source of truth; drop the legacy single groupBy param.
        prev.set("groupDims", dims.join(","));
        prev.delete("groupBy");
        prev.set("page", "1");
        return prev;
      });
    },
    [setSearchParams],
  );

  // Apply a saved view: restore its URL state (grouping/filters/range) and the
  // visible columns (which live outside the URL) in one go.
  const applyView = useCallback(
    (view: SavedView) => {
      setVisibleColumns(view.visibleColumns);
      setSearchParams((prev) => {
        prev.set("groupDims", view.groupDims.join(","));
        prev.delete("groupBy");
        prev.set("dateRange", view.dateRange);
        if (view.filters) prev.set("filters", view.filters);
        else prev.delete("filters");
        // A view saved before column conditions were savable had none.
        if (view.where) prev.set("where", view.where);
        else prev.delete("where");
        if (view.customFrom) prev.set("from", view.customFrom);
        else prev.delete("from");
        if (view.customTo) prev.set("to", view.customTo);
        else prev.delete("to");
        prev.set("page", "1");
        return prev;
      });
    },
    [setSearchParams],
  );

  return (
    <div className="flex h-full w-full bg-background overflow-clip">
      <main className="flex-1 flex flex-col space-y-8 min-w-0">
        <div className="flex flex-col gap-1">
          <h1 className="text-3xl font-bold tracking-tight">
            Clicks Dashboard
          </h1>
          <p className="text-muted-foreground">Real-time clicks metrics.</p>
        </div>

        {/* Full width, not tucked into the heading column: five badges wrapped
            onto three lines there and pushed the table down. */}
        <div className="flex flex-wrap items-center gap-2">
          <PipelineFreshness />
          <GscFreshnessBadge />
        </div>

        <div className="mb-4">
          <ClicksChart
            params={params}
            focus={chartFocus}
            onClearFocus={() => setChartFocus(null)}
            compareValues={chartCompareValues}
            compareParts={compareParts}
            selectedCount={compareValues.length}
            onClearCompare={() => setCompareValues([])}
          />
        </div>

        {/* Table controls sit right above the table, below the chart. */}
        <div className="flex flex-wrap items-center gap-4">
          <DateRangeFilter
            dateRange={dateRange}
            onDateRangeChange={(v) => handleParamChange("dateRange", v)}
            compareEnabled={compareEnabled}
            onCompareChange={(v) => handleParamChange("compare", v.toString())}
            customFrom={customFrom}
            customTo={customTo}
            onCustomDateChange={(type, date) => handleParamChange(type, date)}
          />
          <GroupByFilter value={groupDims} onChange={handleGroupDimsChange} />
          <MetricsFilter
            selectedMetrics={visibleColumns}
            onMetricsChange={setVisibleColumns}
            customMetrics={customMetrics}
            applies={(id) => appliesToGrouping(id, columnContext.dims)}
            onGroupByDomain={() => handleGroupDimsChange(["domain"])}
            defaultColumns={DEFAULT_VISIBLE_METRICS}
          />
          {/* Picked columns this grouping has no values for: the grid leaves
              them out or blank, so say so instead of a column that is just gone (4E). */}
          {attrFailed.length ? (
            <span className="text-xs text-amber-700 dark:text-amber-300">{attrFailed.join(", ")} columns could not load</span>
          ) : null}
          {(() => {
            const off = visibleColumns.filter((id) => !appliesToGrouping(id, columnContext.dims));
            if (!off.length) return null;
            return (
              <span
                className="text-xs text-muted-foreground"
                title={off.map((id) => (id === "brand" ? "Brand" : id === "campaign_group" ? "Campaign Group" : metricLabel(id))).join(", ")}
              >
                {off.length} {off.length === 1 ? "column doesn't" : "columns don't"} apply to this grouping ·{" "}
                <button
                  type="button"
                  onClick={() => handleGroupDimsChange(["domain"])}
                  className="cursor-pointer text-accent-foreground underline-offset-2 hover:underline"
                >
                  Group by Domain
                </button>
              </span>
            );
          })()}
          <CustomMetricsManager
            metrics={customMetrics}
            onChange={changeCustomMetrics}
          />
          <SavedViews
            current={{
              groupDims,
              visibleColumns,
              filters: searchParams.get("filters") ?? "",
              where: searchParams.get("where") ?? "",
              dateRange,
              customFrom,
              customTo,
            }}
            onApply={applyView}
          />
        </div>

        {/* Filters in one row above the table, values fetched per field on
            demand (FilterBar). Replaces the sidebar that loaded every value
            of every field up front and held the page until it had. */}
        <FilterBar
          params={params}
          columnFilters={columnFilters}
          columnContext={columnContext}
          columns={columnOptions}
        />

        {isStatsError ? (
          <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-foreground">
            <p className="font-medium text-destructive">
              This view was too heavy to load and timed out.
            </p>
            <p className="mt-1 text-muted-foreground">
              Try a shorter date range, fewer grouping dimensions, or narrower
              filters, then it will load. The controls above stay active.
            </p>
          </div>
        ) : null}

        {isLoadingData && (
          <LoadingOverlay
            label={
              dateRange === "all_time"
                ? "Calculating All time — this takes a few seconds…"
                : "Loading data…"
            }
          />
        )}

        <div
          className={cn(
            "transition-opacity",
            isLoadingData && "opacity-50 pointer-events-none",
          )}
        >
          <ClicksGrid
            data={mergedData}
            previousData={previousData ?? undefined}
            pageSize={limit}
            onPageSizeChange={(v) => handleParamChange("limit", String(v))}
            visibleColumns={visibleColumns}
            groupBy={groupBy}
            groupDims={groupDims}
            onPrimaryDimChange={(dim) => handleGroupDimsChange([dim])}
            compareEnabled={compareEnabled}
            dateRange={dateRange}
            customFrom={customFrom}
            customTo={customTo}
            activeFilters={params.activeFilters}
            onVisibleColumnsChange={setVisibleColumns}
            onChartFocus={setChartFocus}
            compareValues={compareValues}
            onToggleCompare={toggleCompare}
            onSelectValues={setCompareValues}
            customMetrics={customMetrics}
            columnFilters={columnFilters}
            onColumnFiltersChange={changeColumnFilters}
          />
        </div>
      </main>
    </div>
  );
}
